import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ApiResponse } from '../../../core/api/api-response';
import { GatilhoService } from '../../../core/gatilho/gatilho.service';
import { GatilhoEmissao, SalvarGatilhoPayload } from '../../../core/gatilho/gatilho.model';
import { CupomService } from '../../../core/cupom/cupom.service';
import { CupomListado } from '../../../core/cupom/cupom.model';
import { CatalogoService } from '../../../core/catalogo/catalogo.service';
import { PlanoB2cItem, PlanoB2bItem } from '../../../core/catalogo/catalogo.model';

const EVENTOS = [
  { value: 'INDICACAO_PF',    label: 'Geral' },
  { value: 'ASSINATURA_B2C',  label: 'Assinatura B2C' },
  { value: 'COLABORADOR_B2B', label: 'Colaborador B2B' },
  { value: 'EMISSAO_MANUAL',  label: 'Emissão manual' },
];

const TIPOS_CLIENTE = [
  { value: '',  label: 'Qualquer' },
  { value: 'P', label: 'Particular' },
  { value: 'A', label: 'Assinante (B2C)' },
  { value: 'E', label: 'Empresa (B2B)' },
];

@Component({
  selector: 'app-gatilho-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="page" [formGroup]="form">
      <header class="page__top">
        <div class="page__heading">
          <h1 class="page__title">{{ id() ? 'Editar gatilho #' + id() : 'Novo gatilho' }}</h1>
          <p class="page__sub">{{ id() ? 'Atualizar regra de emissao' : 'Definir quando emitir um cupom' }}</p>
        </div>
        <div class="page__actions">
          <button type="button" class="btn" (click)="cancelar()">Cancelar</button>
        </div>
      </header>

      @if (loading()) {
        <div class="state">Carregando…</div>
      } @else {
        <section class="panel">
          <h2 class="panel__title">Identificacao</h2>
          <label class="fld">
            <span>Nome do gatilho</span>
            <input type="text" formControlName="dsNome" placeholder="Ex: Checkup Gratis, Antecipa 15%" maxlength="100" />
            <span class="fld__hint">Nome amigavel para identificar este gatilho na listagem</span>
          </label>
        </section>

        <section class="panel">
          <h2 class="panel__title">Quando dispara</h2>

          <div class="row">
            <label class="fld">
              <span>Evento</span>
              <select formControlName="ieEvento">
                @for (e of EVENTOS; track e.value) {
                  <option [value]="e.value">{{ e.label }}</option>
                }
              </select>
              <span class="fld__hint">Acao que aciona a emissao do cupom</span>
            </label>
            <label class="fld">
              <span>Tipo de indicacao</span>
              <input type="number" formControlName="nrSeqTipoIndicPf" placeholder="Ex: 127" />
              <span class="fld__hint">Apenas para evento Geral</span>
            </label>
          </div>
        </section>

        <section class="panel">
          <h2 class="panel__title">Filtros do cliente</h2>

          <label class="fld">
            <span>Tipo de cliente</span>
            <select formControlName="ieTipoCliente">
              @for (t of TIPOS_CLIENTE; track t.value) {
                <option [value]="t.value">{{ t.label }}</option>
              }
            </select>
            <span class="fld__hint">Filtra o perfil do paciente que recebe o cupom</span>
          </label>

          @if (form.value.ieTipoCliente === 'A' || form.value.ieTipoCliente === '') {
            <div class="fld">
              <span>Planos B2C</span>
              <div class="plano-actions">
                <button type="button" class="btn btn--pick" (click)="modalPlanosB2c.set(true)">
                  Selecionar planos...
                  @if (planosB2cSelecionados().length > 0) {
                    <span class="btn__badge">{{ planosB2cSelecionados().length }}</span>
                  }
                </button>
                @if (planosB2cSelecionados().length > 0) {
                  <button type="button" class="btn btn--link" (click)="verAssociadosB2c.set(!verAssociadosB2c())">
                    {{ verAssociadosB2c() ? 'Ocultar' : 'Ver' }} {{ planosB2cSelecionados().length }} plano(s) associado(s)
                  </button>
                  @if (verAssociadosB2c()) {
                    <div class="assoc-list">
                      @for (p of planosB2cSelecionados(); track p.cdPlano) {
                        <div class="assoc-item">
                          <span class="assoc-code">{{ p.cdPlano }}</span>
                          <span class="assoc-nome">{{ p.dsPlano }}</span>
                          <button type="button" class="assoc-rm" (click)="togglePlanoB2c(p)">x</button>
                        </div>
                      }
                    </div>
                  }
                } @else {
                  <div class="muted-sm">Nenhum — aceita qualquer plano B2C</div>
                }
              </div>
            </div>
          }

          @if (form.value.ieTipoCliente === 'E' || form.value.ieTipoCliente === '') {
            <div class="fld">
              <span>Planos B2B</span>
              <div class="plano-actions">
                <button type="button" class="btn btn--pick" (click)="modalPlanosB2b.set(true)">
                  Selecionar planos...
                  @if (planosB2bSelecionados().length > 0) {
                    <span class="btn__badge">{{ planosB2bSelecionados().length }}</span>
                  }
                </button>
                @if (planosB2bSelecionados().length > 0) {
                  <button type="button" class="btn btn--link" (click)="verAssociadosB2b.set(!verAssociadosB2b())">
                    {{ verAssociadosB2b() ? 'Ocultar' : 'Ver' }} {{ planosB2bSelecionados().length }} plano(s) associado(s)
                  </button>
                  @if (verAssociadosB2b()) {
                    <div class="assoc-list">
                      @for (p of planosB2bSelecionados(); track p.id) {
                        <div class="assoc-item">
                          <span class="assoc-code">{{ p.id }}</span>
                          <span class="assoc-nome">{{ p.dsPlan }}</span>
                          <button type="button" class="assoc-rm" (click)="togglePlanoB2b(p)">x</button>
                        </div>
                      }
                    </div>
                  }
                } @else {
                  <div class="muted-sm">Nenhum — aceita qualquer plano B2B</div>
                }
              </div>
            </div>
          }

          @if (modalPlanosB2c()) {
            <div class="overlay" (click)="modalPlanosB2c.set(false)">
              <div class="modal" (click)="$event.stopPropagation()">
                <div class="modal__header">
                  <h3 class="modal__title">Selecionar planos B2C</h3>
                  <button type="button" class="modal__close" (click)="modalPlanosB2c.set(false)">x</button>
                </div>
                <div class="modal__search">
                  <input type="text" placeholder="Buscar plano..." [(ngModel)]="buscaPlanoB2c" [ngModelOptions]="{standalone: true}" />
                </div>
                <div class="modal__list">
                  @for (p of planosB2cFiltrados(); track p.cdPlano) {
                    <label class="modal__item">
                      <input type="checkbox" [checked]="isPlanoB2cSelecionado(p.cdPlano)" (change)="togglePlanoB2c(p)" />
                      <span class="modal__code">{{ p.cdPlano }}</span>
                      <span>{{ p.dsPlano }}</span>
                    </label>
                  }
                  @if (planosB2cFiltrados().length === 0) {
                    <div class="modal__empty">Nenhum plano encontrado.</div>
                  }
                </div>
                <div class="modal__footer">
                  <span class="muted-sm">{{ planosB2cSelecionados().length }} selecionado(s)</span>
                  <button type="button" class="btn btn--primary" (click)="modalPlanosB2c.set(false)">Confirmar</button>
                </div>
              </div>
            </div>
          }

          @if (modalPlanosB2b()) {
            <div class="overlay" (click)="modalPlanosB2b.set(false)">
              <div class="modal" (click)="$event.stopPropagation()">
                <div class="modal__header">
                  <h3 class="modal__title">Selecionar planos B2B</h3>
                  <button type="button" class="modal__close" (click)="modalPlanosB2b.set(false)">x</button>
                </div>
                <div class="modal__search">
                  <input type="text" placeholder="Buscar plano..." [(ngModel)]="buscaPlanoB2b" [ngModelOptions]="{standalone: true}" />
                </div>
                <div class="modal__list">
                  @for (p of planosB2bFiltrados(); track p.id) {
                    <label class="modal__item">
                      <input type="checkbox" [checked]="isPlanoB2bSelecionado(p.id)" (change)="togglePlanoB2b(p)" />
                      <span class="modal__code">{{ p.id }}</span>
                      <span>{{ p.dsPlan }}</span>
                    </label>
                  }
                  @if (planosB2bFiltrados().length === 0) {
                    <div class="modal__empty">Nenhum plano encontrado.</div>
                  }
                </div>
                <div class="modal__footer">
                  <span class="muted-sm">{{ planosB2bSelecionados().length }} selecionado(s)</span>
                  <button type="button" class="btn btn--primary" (click)="modalPlanosB2b.set(false)">Confirmar</button>
                </div>
              </div>
            </div>
          }
        </section>

        <section class="panel">
          <h2 class="panel__title">Cupom a emitir</h2>

          <label class="fld fld--check">
            <input type="checkbox" formControlName="ieCupomDinamico" />
            <span>Cupom dinamico (operador digita o codigo no momento)</span>
          </label>

          @if (!form.value.ieCupomDinamico) {
            <label class="fld">
              <span>Cupom vinculado</span>
              <select formControlName="nrSeqCupom">
                <option [ngValue]="null">Selecionar cupom...</option>
                @for (c of cupons(); track c.nrSequencia) {
                  <option [ngValue]="c.nrSequencia">{{ c.codCupom }} ({{ c.dsCupom }})</option>
                }
              </select>
            </label>
          }
        </section>

        <section class="panel">
          <h2 class="panel__title">Vigencia e validacoes</h2>

          <div class="row">
            <label class="fld">
              <span>Dias de vigencia</span>
              <input type="number" formControlName="nrDiasVigencia" min="1" />
              <span class="fld__hint">Quanto tempo o cupom emitido fica valido</span>
            </label>
            <label class="fld">
              <span>Dias de liberacao</span>
              <input type="number" formControlName="nrDiasLiberacao" min="0" />
              <span class="fld__hint">Carencia antes do cupom poder ser usado</span>
            </label>
          </div>

          <label class="fld fld--check">
            <input type="checkbox" formControlName="ieValidarReativacao" />
            <span>So emitir se cliente esta em campanha de reativacao</span>
          </label>

          <label class="fld fld--check">
            <input type="checkbox" formControlName="ieValidarAjusteCheckup" />
            <span>So emitir se passou pela validacao de ajuste de check-up</span>
          </label>
        </section>

        <section class="panel">
          <h2 class="panel__title">Observacao</h2>
          <label class="fld">
            <textarea formControlName="dsObservacao" rows="3" placeholder="Texto livre para documentacao da regra"></textarea>
          </label>
        </section>

        @if (erro()) {
          <div class="alert alert--erro">{{ erro() }}</div>
        }

        <div class="acoes">
          <button type="button" class="btn" (click)="cancelar()" [disabled]="salvando()">Cancelar</button>
          <button type="button" class="btn btn--primary" (click)="salvar()" [disabled]="salvando() || form.invalid">
            {{ salvando() ? 'Salvando…' : (id() ? 'Salvar alterações' : 'Criar gatilho') }}
          </button>
        </div>

        @if (registro(); as r) {
          <div class="audit-footer">
            <span class="audit-footer__icon" aria-hidden="true">i</span>
            <div class="audit-footer__lines">
              @if (r.nmUsuarioNrec || r.dtAtualizacaoNrec) {
                <p><span class="audit-footer__lbl">Criado por</span> <strong>{{ r.nmPessoaCriacao || r.nmUsuarioNrec || '-' }}</strong> em {{ r.dtAtualizacaoNrec | date:'dd/MM/yyyy' }} às {{ r.dtAtualizacaoNrec | date:'HH:mm' }} (UTC-03:00)</p>
              }
              <p><span class="audit-footer__lbl">Modificado por</span> <strong>{{ r.nmPessoaModificacao || r.nmUsuario || '-' }}</strong> em {{ r.dtAtualizacao | date:'dd/MM/yyyy' }} às {{ r.dtAtualizacao | date:'HH:mm' }} (UTC-03:00)</p>
            </div>
          </div>
        }
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .page { padding: 32px 40px; max-width: 880px; margin: 0 auto; font-family: var(--font-sans); color: rgb(var(--color-ink)); }
    .page__top { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 1px solid rgb(var(--color-line)); margin-bottom: 20px; gap: 16px; }
    .page__heading { display: flex; flex-direction: column; gap: 2px; }
    .page__title { margin: 0; font-size: 24px; font-weight: 600; letter-spacing: -0.02em; }
    .page__sub { margin: 0; color: rgb(var(--color-ink-3)); font-size: 13px; font-weight: 400; }
    .page__actions { flex-shrink: 0; padding-top: 4px; }

    .btn { height: 34px; padding: 0 14px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius); background: rgb(var(--color-paper)); color: rgb(var(--color-ink)); cursor: pointer; font-size: 13px; font-weight: 500; font-family: inherit; display: inline-flex; align-items: center; transition: background-color 120ms, border-color 120ms, color 120ms; }
    .btn:hover:not(:disabled) { background: rgb(var(--color-hover)); }
    .btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .btn--primary { background: rgb(var(--color-accent)); color: #fff; border-color: rgb(var(--color-accent)); font-weight: 600; }
    .btn--primary:hover:not(:disabled) { background: rgb(var(--color-accent-2)); }

    .panel { background: rgb(var(--color-paper)); border: 1px solid rgb(var(--color-line)); border-radius: var(--radius-lg); padding: 24px; margin-bottom: 16px; }
    .panel__title { margin: 0 0 14px 0; font-size: 14px; font-weight: 600; color: rgb(var(--color-ink-2)); text-transform: uppercase; letter-spacing: 0.04em; }

    .row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 12px; }
    .fld { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
    .fld span { font-size: 12px; color: rgb(var(--color-ink-2)); font-weight: 500; }
    .fld input, .fld select, .fld textarea {
      padding: 9px 12px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius);
      background: rgb(var(--color-paper)); color: rgb(var(--color-ink)); font-size: 14px;
      font-family: inherit; outline: none;
    }
    .fld input:focus, .fld select:focus, .fld textarea:focus { border-color: rgb(var(--color-accent)); }
    .fld__hint { font-size: 11px; color: rgb(var(--color-ink-3)); font-weight: 400; }
    .fld--check { flex-direction: row; align-items: center; gap: 10px; cursor: pointer; }
    .fld--check input { accent-color: rgb(var(--color-accent)); width: 16px; height: 16px; }

    .alert--erro { background: rgb(var(--color-danger-bg)); border: 1px solid rgb(var(--color-danger)); color: rgb(var(--color-danger)); padding: 12px 16px; border-radius: var(--radius); margin-bottom: 16px; }
    .state { padding: 28px; background: rgb(var(--color-paper)); border: 1px solid rgb(var(--color-line)); border-radius: var(--radius); color: rgb(var(--color-ink-3)); text-align: center; }
    .acoes { display: flex; justify-content: flex-end; gap: 10px; padding-top: 16px; border-top: 1px solid rgb(var(--color-line)); }

    .plano-actions { display: flex; flex-direction: column; gap: 6px; }
    .btn--pick { width: 100%; text-align: left; display: flex; align-items: center; justify-content: space-between; }
    .btn__badge { background: rgb(var(--color-accent)); color: #fff; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 999px; }
    .btn--link { background: none; border: none; color: rgb(var(--color-accent)); cursor: pointer; font-size: 12px; font-weight: 500; padding: 0; text-align: left; text-decoration: underline; }
    .btn--link:hover { color: rgb(var(--color-accent-2)); }
    .muted-sm { font-size: 12px; color: rgb(var(--color-ink-3)); }

    .assoc-list { margin-top: 8px; border: 1px solid rgb(var(--color-line)); border-radius: var(--radius); max-height: 200px; overflow-y: auto; }
    .assoc-item { display: flex; align-items: center; gap: 10px; padding: 7px 12px; border-bottom: 1px solid rgb(var(--color-line)); font-size: 13px; }
    .assoc-item:last-child { border-bottom: none; }
    .assoc-item:hover { background: rgb(var(--color-hover)); }
    .assoc-code { font-size: 11px; color: rgb(var(--color-ink-3)); font-variant-numeric: tabular-nums; min-width: 32px; }
    .assoc-nome { flex: 1; }
    .assoc-rm { background: none; border: none; color: rgb(var(--color-ink-3)); cursor: pointer; font-size: 14px; font-weight: 700; padding: 0 4px; }
    .assoc-rm:hover { color: rgb(var(--color-danger)); }

    .overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
    .modal { background: rgb(var(--color-paper)); border-radius: var(--radius-lg); width: 520px; max-width: 92vw; max-height: 80vh; display: flex; flex-direction: column; box-shadow: 0 12px 40px rgba(0,0,0,.2); }
    .modal__header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid rgb(var(--color-line)); }
    .modal__title { margin: 0; font-size: 16px; font-weight: 600; }
    .modal__close { background: none; border: none; font-size: 20px; cursor: pointer; color: rgb(var(--color-ink-3)); padding: 0 4px; }
    .modal__close:hover { color: rgb(var(--color-ink)); }
    .modal__search { padding: 12px 20px; border-bottom: 1px solid rgb(var(--color-line)); }
    .modal__search input { width: 100%; padding: 8px 12px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius); font-size: 14px; font-family: inherit; outline: none; background: rgb(var(--color-paper)); color: rgb(var(--color-ink)); }
    .modal__search input:focus { border-color: rgb(var(--color-accent)); }
    .modal__list { flex: 1; overflow-y: auto; padding: 8px 0; }
    .modal__item { display: flex; align-items: center; gap: 10px; padding: 8px 20px; cursor: pointer; font-size: 13px; }
    .modal__item:hover { background: rgb(var(--color-hover)); }
    .modal__item input[type="checkbox"] { accent-color: rgb(var(--color-accent)); width: 16px; height: 16px; flex-shrink: 0; }
    .modal__code { font-size: 11px; color: rgb(var(--color-ink-3)); font-variant-numeric: tabular-nums; min-width: 36px; }
    .modal__empty { padding: 20px; text-align: center; color: rgb(var(--color-ink-3)); font-size: 13px; }
    .modal__footer { display: flex; justify-content: space-between; align-items: center; padding: 12px 20px; border-top: 1px solid rgb(var(--color-line)); }

    .audit-footer { display: flex; align-items: flex-start; gap: 10px; margin-top: 20px; padding: 14px 4px 4px; border-top: 1px solid rgb(var(--color-line)); color: rgb(var(--color-ink) / 0.7); font-size: 13px; line-height: 1.5; }
    .audit-footer__icon { flex: none; width: 18px; height: 18px; margin-top: 1px; border-radius: 50%; display: grid; place-items: center; background: rgb(var(--color-info)); color: #fff; font-style: italic; font-size: 12px; font-weight: 700; line-height: 1; }
    .audit-footer__lines { display: flex; flex-direction: column; gap: 2px; }
    .audit-footer__lines p { margin: 0; }
    .audit-footer__lbl { color: rgb(var(--color-ink) / 0.55); }
    .audit-footer strong { color: rgb(var(--color-ink)); font-weight: 600; }
  `],
})
export class GatilhoFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly svc = inject(GatilhoService);
  private readonly cupomSvc = inject(CupomService);
  private readonly catalogoSvc = inject(CatalogoService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly EVENTOS = EVENTOS;
  protected readonly TIPOS_CLIENTE = TIPOS_CLIENTE;

  protected readonly id = signal<number | null>(null);
  protected readonly registro = signal<GatilhoEmissao | null>(null);
  protected readonly cupons = signal<CupomListado[]>([]);

  protected readonly todosPlanosB2c = signal<PlanoB2cItem[]>([]);
  protected readonly planosB2cSelecionados = signal<PlanoB2cItem[]>([]);
  protected readonly modalPlanosB2c = signal(false);
  protected readonly verAssociadosB2c = signal(false);
  protected buscaPlanoB2c = '';
  protected readonly planosB2cFiltrados = computed(() => {
    const termo = this.buscaPlanoB2c.toLowerCase().trim();
    const todos = this.todosPlanosB2c();
    if (!termo) return todos;
    return todos.filter(p =>
      p.dsPlano.toLowerCase().includes(termo) || String(p.cdPlano).includes(termo)
    );
  });

  protected readonly todosPlanosB2b = signal<PlanoB2bItem[]>([]);
  protected readonly planosB2bSelecionados = signal<PlanoB2bItem[]>([]);
  protected readonly modalPlanosB2b = signal(false);
  protected readonly verAssociadosB2b = signal(false);
  protected buscaPlanoB2b = '';
  protected readonly planosB2bFiltrados = computed(() => {
    const termo = this.buscaPlanoB2b.toLowerCase().trim();
    const todos = this.todosPlanosB2b();
    if (!termo) return todos;
    return todos.filter(p =>
      p.dsPlan.toLowerCase().includes(termo) || String(p.id).includes(termo)
    );
  });
  protected readonly loading = signal(true);
  protected readonly salvando = signal(false);
  protected readonly erro = signal<string | null>(null);

  protected readonly form = this.fb.nonNullable.group({
    dsNome: [''],
    ieEvento: ['INDICACAO_PF' as string, [Validators.required]],
    nrSeqTipoIndicPf: [null as number | null],
    ieTipoCliente: [''],
    dsPlanosB2c: [''],
    dsPlanosB2b: [''],
    nrSeqCupom: [null as number | null],
    ieCupomDinamico: [false],
    ieValidarReativacao: [false],
    ieValidarAjusteCheckup: [false],
    nrDiasVigencia: [30, [Validators.required, Validators.min(1)]],
    nrDiasLiberacao: [0, [Validators.min(0)]],
    ieSituacao: ['A'],
    dsObservacao: [''],
  });

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam ? Number(idParam) : null;
    this.id.set(id);

    this.cupomSvc.listar({ ieSituacao: 'A' }).subscribe({
      next: res => this.cupons.set(res.data?.items ?? []),
      error: () => {},
    });

    this.catalogoSvc.listarPlanosB2c().subscribe({
      next: res => {
        const planos = res.data ?? [];
        this.todosPlanosB2c.set(planos);
        this.syncPlanosB2cSelecionados(planos);
      },
      error: () => {},
    });

    this.catalogoSvc.listarPlanosB2b().subscribe({
      next: res => {
        const planos = res.data ?? [];
        this.todosPlanosB2b.set(planos);
        this.syncPlanosB2bSelecionados(planos);
      },
      error: () => {},
    });

    if (id) {
      this.svc.obter(id).subscribe({
        next: res => {
          const g = res.data;
          if (g) {
            this.registro.set(g);
            this.form.patchValue({
              dsNome: g.dsNome ?? '',
              ieEvento: g.ieEvento,
              nrSeqTipoIndicPf: g.nrSeqTipoIndicPf ?? null,
              ieTipoCliente: g.ieTipoCliente ?? '',
              dsPlanosB2c: g.dsPlanosB2c ?? '',
              dsPlanosB2b: g.dsPlanosB2b ?? '',
              nrSeqCupom: g.nrSeqCupom ?? null,
              ieCupomDinamico: g.ieCupomDinamico === 'S',
              ieValidarReativacao: g.ieValidarReativacao === 'S',
              ieValidarAjusteCheckup: g.ieValidarAjusteCheckup === 'S',
              nrDiasVigencia: g.nrDiasVigencia,
              nrDiasLiberacao: g.nrDiasLiberacao,
              ieSituacao: g.ieSituacao,
              dsObservacao: g.dsObservacao ?? '',
            });
            this.syncPlanosB2cSelecionados(this.todosPlanosB2c());
            this.syncPlanosB2bSelecionados(this.todosPlanosB2b());
          }
          this.loading.set(false);
        },
        error: e => { this.erro.set(e?.message ?? 'Falha ao carregar gatilho.'); this.loading.set(false); },
      });
    } else {
      this.loading.set(false);
    }
  }

  salvar(): void {
    if (this.salvando() || this.form.invalid) return;
    const v = this.form.getRawValue();

    if (!v.ieCupomDinamico && !v.nrSeqCupom) {
      this.erro.set('Selecione um cupom ou marque "Cupom dinâmico".');
      return;
    }

    const payload: SalvarGatilhoPayload = {
      dsNome: v.dsNome || null,
      ieEvento: v.ieEvento,
      nrSeqTipoIndicPf: v.nrSeqTipoIndicPf,
      ieTipoCliente: v.ieTipoCliente || null,
      dsPlanosB2c: v.dsPlanosB2c || null,
      dsPlanosB2b: v.dsPlanosB2b || null,
      nrSeqCupom: v.ieCupomDinamico ? null : v.nrSeqCupom,
      ieCupomDinamico: v.ieCupomDinamico ? 'S' : 'N',
      ieValidarReativacao: v.ieValidarReativacao ? 'S' : 'N',
      ieValidarAjusteCheckup: v.ieValidarAjusteCheckup ? 'S' : 'N',
      nrDiasVigencia: v.nrDiasVigencia,
      nrDiasLiberacao: v.nrDiasLiberacao,
      ieSituacao: v.ieSituacao,
      dsObservacao: v.dsObservacao || null,
    };

    this.salvando.set(true);
    this.erro.set(null);

    const obs: Observable<ApiResponse<number | boolean>> = this.id()
      ? this.svc.atualizar(this.id()!, payload)
      : this.svc.criar(payload);

    obs.subscribe({
      next: res => {
        this.salvando.set(false);
        if (res.status >= 200 && res.status < 300) {
          this.router.navigateByUrl('/gatilhos');
        } else {
          this.erro.set(res.error?.message ?? 'Falha ao salvar gatilho.');
        }
      },
      error: e => {
        this.salvando.set(false);
        this.erro.set(e?.error?.error?.message ?? e?.message ?? 'Falha ao salvar gatilho.');
      },
    });
  }

  isPlanoB2cSelecionado(cdPlano: number): boolean {
    return this.planosB2cSelecionados().some(p => p.cdPlano === cdPlano);
  }

  togglePlanoB2c(plano: PlanoB2cItem): void {
    if (this.isPlanoB2cSelecionado(plano.cdPlano)) {
      this.planosB2cSelecionados.update(arr => arr.filter(p => p.cdPlano !== plano.cdPlano));
    } else {
      this.planosB2cSelecionados.update(arr => [...arr, plano]);
    }
    const csv = this.planosB2cSelecionados().map(p => p.cdPlano).join(',');
    this.form.patchValue({ dsPlanosB2c: csv || '' });
  }

  isPlanoB2bSelecionado(id: number): boolean {
    return this.planosB2bSelecionados().some(p => p.id === id);
  }

  togglePlanoB2b(plano: PlanoB2bItem): void {
    if (this.isPlanoB2bSelecionado(plano.id)) {
      this.planosB2bSelecionados.update(arr => arr.filter(p => p.id !== plano.id));
    } else {
      this.planosB2bSelecionados.update(arr => [...arr, plano]);
    }
    const csv = this.planosB2bSelecionados().map(p => p.id).join(',');
    this.form.patchValue({ dsPlanosB2b: csv || '' });
  }

  private syncPlanosB2cSelecionados(planos: PlanoB2cItem[]): void {
    const csv = this.form.value.dsPlanosB2c;
    if (!csv) return;
    const ids = csv.split(',').map(s => Number(s.trim())).filter(n => n > 0);
    this.planosB2cSelecionados.set(planos.filter(p => ids.includes(p.cdPlano)));
  }

  private syncPlanosB2bSelecionados(planos: PlanoB2bItem[]): void {
    const csv = this.form.value.dsPlanosB2b;
    if (!csv) return;
    const ids = csv.split(',').map(s => Number(s.trim())).filter(n => n > 0);
    this.planosB2bSelecionados.set(planos.filter(p => ids.includes(p.id)));
  }

  cancelar(): void { this.router.navigateByUrl('/gatilhos'); }
}
